import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import { bulkRefreshZoomLinks, getAdminSession, getAdminSessions } from '@/lib/api-calls';
import { partitionBy } from '@airreading/common';
import { AdminSession } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { z } from 'zod';

export interface BulkRefreshZoomLinksProps {
  sessions: AdminSession[];
  trigger: ReactNode;
  onSuccess?: () => void;
}

const formSchema = z.object({});

export const BulkRefreshZoomLinks = ({ sessions, trigger, onSuccess }: BulkRefreshZoomLinksProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: bulkRefreshZoomLinks,
  });

  const isValidSession = (session: AdminSession): boolean => {
    return (
      Boolean(session.coach?.coachProfile?.zoomPersonalMeetingId) &&
      session.status !== 'archived' &&
      session.status !== 'cancelled'
    );
  };

  const [validSessions, invalidSessions] = partitionBy(sessions, isValidSession);

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({ sessionIds: validSessions.map((session) => session.id) })
      .then(({ successSessionIds, errors, failedSessionIds }) => {
        if (successSessionIds.length > 0) {
          toast({
            title: `Refreshed zoom links for ${successSessionIds.length} sessions`,
            description: 'The session zoom links have been updated',
          });
        }
        if (failedSessionIds.length > 0) {
          toast({
            title: `Failed to refresh zoom links for ${failedSessionIds.length} sessions`,
            variant: 'destructive',
          });
        }
        if (errors.length > 0) {
          toast({
            title: `Errors`,
            variant: 'destructive',
            duration: 15000,
            description: (
              <ul>
                {errors.map((e) => (
                  <li>{e}</li>
                ))}
              </ul>
            ),
          });
        }
        queryClient.invalidateQueries(getAdminSessions());
        for (const id of successSessionIds) {
          queryClient.invalidateQueries(getAdminSession(id));
        }
        closeModal();
        onSuccess?.();
      })
      .catch(() => {
        toast({ title: 'Failed to refresh zoom links', variant: 'destructive' });
      });
  };

  return (
    <FormModal
      modalTitle="Refresh Zoom Host Links"
      formSchema={formSchema}
      trigger={trigger}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: validSessions.length === 0 || isPending,
        loading: isPending,
      }}
      confirmBtnLabel="Refresh zoom links"
    >
      {() => {
        return (
          <div className="my-6">
            {validSessions.length === 0 ? (
              <div>
                None of the selected sessions can have their zoom links refreshed. Sessions must have an assigned
                teacher with an activated Zoom account.
              </div>
            ) : (
              <>
                {invalidSessions.length > 0 && (
                  <div className="mb-4">
                    {`There are ${invalidSessions.length} sessions that cannot have their zoom links refreshed. `}
                    {`These sessions either have no assigned teacher or the assigned teacher doesn't have an activated Zoom account. Or they are canceled or archived.`}
                  </div>
                )}

                {validSessions.length > 0 && (
                  <div>
                    {`Are you sure you want to refresh zoom links for ${validSessions.length} sessions?`}
                    <p className="mt-2 text-sm text-muted-foreground">
                      This will call the Zoom Get Meeting API, update the Zoom session status, and refresh the Zoom host
                      links in the table.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};
